import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, Search, Share2, Copy, Check, ChevronDown,
  ArrowRight, Sparkles, Bell, Calendar, Clock, User, ExternalLink,
} from "lucide-react";
import { PlatformLayout } from "../PlatformLayout";
import { Brand } from "../Brand";
import { AnimatedTitle } from "../AnimatedTitle";
import { useAuth, authApi } from "../authApi";
import { STATUS, CATEGORIES, PHASES, LAST_UPDATE, TIMELINE, MILESTONES, VISION, PRIVATE_KEY } from "./changelogData";

const StatusBadge = ({ status }) => {
  const s = STATUS[status];
  if (!s) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full ring-1 ${s.ring} ${s.text}`} data-testid={`status-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} /> {s.label}
    </span>
  );
};

function LockScreen({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (pw.trim().toUpperCase() === PRIVATE_KEY) onUnlock();
    else setErr("Mot de passe incorrect.");
  };
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="private-lock">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-sm text-center space-y-6">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#C19A6B]/15 flex items-center justify-center"><Lock className="w-6 h-6 text-[#C19A6B]" /></div>
        <div className="space-y-2">
          <h1 className="text-2xl font-light text-white tracking-tight">Espace partenaires <Brand /></h1>
          <p className="text-sm text-zinc-400 font-light">Accès réservé aux administrateurs et conseillers autorisés.</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="password"
            value={pw}
            onChange={(e) => { setPw(e.target.value); setErr(""); }}
            placeholder="Mot de passe"
            data-testid="private-password-input"
            className="w-full bg-white/5 border border-white/15 px-4 py-3 text-center text-white placeholder-zinc-500 focus:outline-none focus:border-[#C19A6B] transition-colors tracking-widest"
          />
          {err && <p className="text-sm text-red-400" data-testid="private-error">{err}</p>}
          <button type="submit" data-testid="private-unlock-btn" className="w-full bg-[#C19A6B] hover:bg-[#d1af84] text-[#050505] font-medium uppercase tracking-wider text-sm py-3 transition-colors">Déverrouiller</button>
        </form>
        <Link to="/" className="inline-block text-xs uppercase tracking-wider text-zinc-500 hover:text-white transition-colors">Retour au site</Link>
      </motion.div>
    </div>
  );
}

function TimelineItem({ item, open, onToggle, isLatest, refEl }) {
  const s = STATUS[item.status];
  const d = new Date(item.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  return (
    <div ref={refEl} className="relative pl-10 sm:pl-14" data-testid={`timeline-item-${item.id}`}>
      {/* ligne + point */}
      <span className="absolute left-[11px] sm:left-[19px] top-2 bottom-[-28px] w-px bg-white/10" />
      <motion.span
        initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className={`absolute left-0 sm:left-2 top-1 w-6 h-6 rounded-full border-2 border-[#050505] ${s?.dot} ring-4 ${s?.ring}`}
      />
      <motion.button
        type="button"
        onClick={onToggle}
        initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={`w-full text-left border transition-colors ${open ? "bg-[#141418] border-[#C19A6B]/50" : "bg-[#111114] border-white/10 hover:border-[#C19A6B]/30"} ${isLatest ? "ring-1 ring-[#C19A6B]/40" : ""}`}
        data-testid={`timeline-toggle-${item.id}`}
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={item.status} />
                {item.category && <span className="text-[11px] uppercase tracking-wider text-[#C19A6B] bg-[#C19A6B]/10 px-2 py-1 rounded-full">{item.category}</span>}
                {item.future && <span className="text-[11px] uppercase tracking-wider text-[#C19A6B] bg-[#C19A6B]/10 px-2 py-1 rounded-full">À venir</span>}
                {isLatest && <span className="text-[10px] uppercase tracking-widest text-[#C19A6B]">● Nouveauté</span>}
              </div>
              <h3 className="text-lg sm:text-xl font-light text-white">{item.icon ? `${item.icon} ` : ""}{item.title}</h3>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 pt-1">
                <span className="flex items-center gap-1.5 text-lg sm:text-xl font-bold text-white"><Calendar className="w-4 h-4 text-[#C19A6B]" /> {d}</span>
                {item.time && <span className="flex items-center gap-1.5 text-lg sm:text-xl font-bold text-white"><Clock className="w-4 h-4 text-[#C19A6B]" /> {item.time}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
                {item.version && <span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> v{item.version}</span>}
                {item.developer && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {item.developer}</span>}
                {item.statusLabel && <span>{item.statusLabel}</span>}
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-[#C19A6B] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </div>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0, marginTop: 0 }}
                animate={{ height: "auto", opacity: 1, marginTop: 16 }}
                exit={{ height: 0, opacity: 0, marginTop: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <p className="text-sm font-light text-zinc-300 leading-relaxed">{item.description}</p>
                {item.benefits?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-widest text-[#C19A6B] mb-2">{item.future ? "Au programme" : "Bénéfices pour les conseillers"}</div>
                    <ul className="space-y-1.5">
                      {item.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-zinc-400"><Check className="w-4 h-4 text-[#C19A6B] mt-0.5 shrink-0" /> {b}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {item.screenshots?.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {item.screenshots.map((src, i) => (<img key={i} src={src} alt="" className="w-full aspect-video object-cover border border-white/10" />))}
                  </div>
                )}
                {item.links?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {item.links.map((l, i) => (<a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#C19A6B] hover:gap-2.5 transition-all">{l.label} <ExternalLink className="w-3.5 h-3.5" /></a>))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.button>
    </div>
  );
}

export default function Private() {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Tous");
  const [openId, setOpenId] = useState(TIMELINE[0]?.id || null);
  const [copied, setCopied] = useState(false);
  const latestRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("key") === PRIVATE_KEY || sessionStorage.getItem("omk_private") === "1") { setUnlocked(true); return; }
    // Accès sans mot de passe : administrateurs uniquement
    if (user === null) return; // auth en cours de vérification
    if (user && (user.role === "admin" || user.role === "super_admin")) { setUnlocked(true); return; }
  }, [user]);

  const unlock = () => { sessionStorage.setItem("omk_private", "1"); setUnlocked(true); };

  const newestRealId = TIMELINE[0]?.id;

  const milestoneItems = useMemo(() => MILESTONES.map((m) => ({
    id: m.id, date: m.date, version: m.version, category: null,
    status: m.status, statusLabel: m.statusLabel, title: m.title,
    description: m.description, benefits: m.items, icon: m.icon, future: true,
  })), []);

  const fullTimeline = useMemo(() => [...[...TIMELINE].reverse(), ...milestoneItems], [milestoneItems]);

  const filtered = useMemo(() => fullTimeline.filter((t) => {
    const okCat = cat === "Tous" || t.category === cat;
    const okQ = q.trim() === "" || (t.title + " " + (t.description || "") + " " + (t.category || "")).toLowerCase().includes(q.toLowerCase());
    return okCat && okQ;
  }), [q, cat, fullTimeline]);

  // Regroupement par grand axe de développement (sections avec titres)
  const grouping = cat === "Tous" && q.trim() === "";
  const groupedPhases = useMemo(() => {
    const byPhase = {};
    filtered.forEach((it) => { const p = it.phase || "autres"; (byPhase[p] = byPhase[p] || []).push(it); });
    return PHASES.filter((p) => (byPhase[p.key] || []).length).map((p) => ({ ...p, items: byPhase[p.key] }));
  }, [filtered]);

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/private?key=${PRIVATE_KEY}` : "";
  const waLink = `https://wa.me/?text=${encodeURIComponent("Suivez l'évolution d'OFF MARKETTE en temps réel : " + shareUrl)}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { /* noop */ }
  };
  const scrollToLatest = () => latestRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  if (!unlocked) return <LockScreen onUnlock={unlock} />;

  const cats = ["Tous", ...CATEGORIES];

  return (
    <PlatformLayout searchDock={false} solidHeader>
      <div className="bg-[#050505] min-h-screen pt-28 sm:pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Notif dernière mise à jour */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-[#C19A6B]/30 bg-[#C19A6B]/5 px-5 py-4 mb-10" data-testid="last-update-bar">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-[#C19A6B]" />
              <div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-400">Dernière mise à jour</div>
                <div className="text-sm text-white">Version {LAST_UPDATE.version} — {LAST_UPDATE.date}</div>
              </div>
            </div>
            <button onClick={scrollToLatest} data-testid="see-news-btn" className="inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-[#050505] font-medium uppercase tracking-wider text-xs px-5 py-2.5 transition-colors">Voir les nouveautés <ArrowRight className="w-4 h-4" /></button>
          </motion.div>

          {/* En-tête */}
          <div className="mb-8">
            <span className="calli text-lg text-[#C19A6B]">Espace partenaires · Journal officiel</span>
            <AnimatedTitle as="h1" className="text-4xl sm:text-5xl font-light tracking-tight text-white mt-2" text="Évolution de la plateforme" />
            <p className="text-base font-light text-zinc-400 mt-3 max-w-2xl">Suivez en temps réel le développement d'<Brand />, découvrez les nouvelles fonctionnalités et les prochaines étapes du projet.</p>
          </div>

          {/* Partage */}
          <div className="flex flex-wrap gap-3 mb-10" data-testid="share-row">
            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-[#052e16] font-medium uppercase tracking-wider text-xs px-5 py-2.5 transition-colors"><Share2 className="w-4 h-4" /> Partager via WhatsApp</a>
            <button onClick={copyLink} data-testid="share-copy" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#C19A6B]/50 text-zinc-200 uppercase tracking-wider text-xs px-5 py-2.5 transition-colors">
              {copied ? <><Check className="w-4 h-4 text-[#C19A6B]" /> Lien copié</> : <><Copy className="w-4 h-4" /> Copier le lien</>}
            </button>
          </div>

          {/* Recherche + filtres */}
          <div className="space-y-4 mb-10">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une fonctionnalité…" data-testid="search-input" className="w-full bg-white/5 border border-white/10 pl-11 pr-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#C19A6B] transition-colors" />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="category-filters">
              {cats.map((c) => (
                <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c}`} className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${cat === c ? "bg-[#C19A6B] border-[#C19A6B] text-[#050505]" : "border-white/15 text-zinc-400 hover:border-[#C19A6B]/40 hover:text-white"}`}>{c}</button>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="space-y-7" data-testid="timeline">
            {filtered.length === 0 ? (
              <p className="text-center text-zinc-500 py-12" data-testid="timeline-empty">Aucune évolution ne correspond à votre recherche.</p>
            ) : grouping ? (
              groupedPhases.map((p) => (
                <section key={p.key} className="pt-4" data-testid={`phase-${p.key}`}>
                  {/* Titre de section — grand axe de développement */}
                  <div className="mb-6">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{p.icon}</span>
                      <h2 className="text-xl sm:text-2xl font-light tracking-tight text-white">{p.title}</h2>
                    </div>
                    <p className="text-sm font-light text-zinc-400 mt-2 max-w-2xl">{p.desc}</p>
                    <div className="grid sm:grid-cols-2 gap-3 mt-4">
                      <div className="border-l-2 border-[#C19A6B] pl-3">
                        <div className="text-[10px] uppercase tracking-widest text-[#C19A6B]">Pour l'acquéreur</div>
                        <p className="text-[13px] text-zinc-300 mt-1">{p.acquereur}</p>
                      </div>
                      <div className="border-l-2 border-white/25 pl-3">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-300">Pour le vendeur</div>
                        <p className="text-[13px] text-zinc-400 mt-1">{p.vendeur}</p>
                      </div>
                    </div>
                    <div className="h-px bg-gradient-to-r from-[#C19A6B]/50 to-transparent mt-5" />
                  </div>
                  <div className="space-y-7">
                    {p.items.map((item) => (
                      <TimelineItem
                        key={item.id}
                        item={item}
                        open={openId === item.id}
                        onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                        isLatest={item.id === newestRealId}
                        refEl={item.id === newestRealId ? latestRef : null}
                      />
                    ))}
                  </div>
                </section>
              ))
            ) : (
              filtered.map((item) => (
                <TimelineItem
                  key={item.id}
                  item={item}
                  open={openId === item.id}
                  onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                  isLatest={item.id === newestRealId && cat === "Tous" && q.trim() === ""}
                  refEl={item.id === newestRealId ? latestRef : null}
                />
              ))
            )}
          </div>

          {/* Vision */}
          <div className="mt-20 border border-[#C19A6B]/20 bg-gradient-to-br from-[#C19A6B]/10 to-transparent p-8 sm:p-10" data-testid="vision">
            <span className="calli text-lg text-[#C19A6B]">La vision</span>
            <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight text-white mt-2 mb-6" text="Et après ?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {VISION.map((v, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: (i % 2) * 0.06 }} className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#C19A6B] shrink-0 mt-2" />
                  <div>
                    <div className="text-white font-light">{v.title}</div>
                    <div className="text-sm text-zinc-500 font-light mt-0.5 leading-relaxed">{v.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
            <p className="text-sm text-zinc-500 font-light mt-8 max-w-2xl">Cette frise évolue à chaque nouvelle version afin d'offrir aux conseillers une vision claire de l'historique, des nouveautés livrées et des prochaines étapes du réseau <Brand />.</p>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
}
